import { TipoEstadoActualSecundarioTrackingCorteEnum, TipoTrackingCorteEnum } from 'config';
import { TrackingCorteEstadosResponseModel } from 'services/models';
import { CADENA_VACIA } from 'utils/constants';
import { parseFlexibleDate } from './formatters/dateFormatter';

/**
 * Constante que contiene el mínimo de clientes sin servicio.
 */
const MINIMO_CLIENTES_SIN_SERVICIO = 0;

/**
 * Motivo homologado por el backend cuando el origen no está determinado.
 */
export const MOTIVO_FALLA_NO_IDENTIFICADA = 'falla aun no identificada';

/**
 * Modelo de vista del dashboard de tracking a partir de la respuesta del API.
 */
export interface OutageDashboardData {
  /**
   * Motivo informado por el tracking; vacío si no hay información de producto
   */
  motivo_corte: string;
  /**
   * Fecha de reposición estimada
   */
  ter: Date | null;
  /**
   * Etapa de pintado / timeline: solo estadoActualSecundario
   */
  etapa_actual: TipoEstadoActualSecundarioTrackingCorteEnum;
  /**
   * Horas de cada etapa del timeline
   */
  hora_etapa: {
    corteConfirmado: Date | null;
    asignacionBrigada: Date | null;
    brigadaEnTerreno: Date | null;
    reposicionEstimada: Date | null;
  };
  /**
   * Clientes que siguen sin servicio; null si no se pueden calcular
   */
  clientes_sin_servicio: number | null;
  /**
   * Clientes ya recuperados
   */
  clientes_recuperados: number | null;
  /**
   * Total de clientes afectados al inicio
   */
  total_afectados: number | null;
  /**
   * Fecha de última actualización del mapa
   */
  ultima_actualizacion_mapa: Date | null;
  /**
   * Hay brigada asignada o en terreno
   */
  hasBrigadaAsignada: boolean;
  /**
   * Tipo de corte del tracking
   */
  tipoCorte: TipoTrackingCorteEnum;
  /**
   * Número de orden asociado al polígono
   */
  numero_orden: string;
  /**
   * Se muestra el gráfico solo si las tres métricas de clientes son numéricas
   */
  mostrarGraficoClientes: boolean;
  /**
   * El corte está normalizado
   */
  esCorteConcluido: boolean;
  /**
   * Hay fecha de reposición estimada
   */
  tieneTer: boolean;
}

/**
 * Convierte una métrica de clientes del API a número.
 * @param value texto numérico enviado por el servicio
 * @returns número finito o null si viene vacío o no es numérico
 */
const parseMetric = (value?: string | null): number | null => {
  if (value === null || value === undefined || String(value).trim() === CADENA_VACIA) {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Indica si el motivo del corte no debe mostrarse como texto de producto.
 * El backend homologa "Origen no determinado" a "Falla aún no identificada".
 * @param motivo texto de motivo del tracking
 * @returns true si no hay motivo informable
 */
export const esMotivoCorteSinInformacion = (motivo?: string | null): boolean => {
  if (motivo === null || motivo === undefined || motivo.trim() === CADENA_VACIA) {
    return true;
  }
  const normalizado = motivo
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, CADENA_VACIA);
  return normalizado === MOTIVO_FALLA_NO_IDENTIFICADA;
};

/**
 * Deja el motivo vacío cuando no hay información de producto.
 * @param motivo texto de motivo del tracking
 * @returns motivo recortado o cadena vacía
 */
const normalizarMotivoCorte = (motivo?: string | null): string => {
  if (esMotivoCorteSinInformacion(motivo)) {
    return CADENA_VACIA;
  }
  return (motivo ?? CADENA_VACIA).trim();
};

/**
 * Arma el título del corte programado o futuro con el motivo fijo y la comuna del KML.
 * @param comuna comuna del polígono
 * @param labels textos con comuna y sin comuna
 * @returns título listo para la card
 */
export const buildTituloCorteProgramado = (
  comuna: string | undefined,
  labels: { conComuna: (c: string) => string; sinComuna: string }
): string => {
  const valor = (comuna || CADENA_VACIA).trim();
  return valor ? labels.conComuna(valor) : labels.sinComuna;
};

/**
 * Mapea la respuesta de tracking y el número de orden del KML al modelo del dashboard.
 * @param api respuesta del servicio de estados del corte
 * @param numeroOrden identificador de la orden asociado al polígono
 * @returns datos listos para pintar el dashboard de imprevisto
 */
export const mapApiResponseToDashboardData = (api: TrackingCorteEstadosResponseModel, numeroOrden: string): OutageDashboardData => {
  const total = parseMetric(api?.clientesAfectadosIniciales);
  const recuperados = parseMetric(api?.clientesRecuperados);
  const sinServicio = total !== null && recuperados !== null ? Math.max(total - recuperados, MINIMO_CLIENTES_SIN_SERVICIO) : null;

  const hasBrigadaAsignada =
    api?.estadoActualSecundario === TipoEstadoActualSecundarioTrackingCorteEnum.BRIGADA_ASIGNADA ||
    api?.estadoActualSecundario === TipoEstadoActualSecundarioTrackingCorteEnum.BRIGADA_EN_TERRENO;

  const ter = parseFlexibleDate(api?.fechaFinCorte);
  const fechaArribo = parseFlexibleDate(api?.fechaArriboBrigada);

  /**
   * El pintado del dashboard usa solo estadoActualSecundario, no estadoActual.
   */
  const esCorteConcluido = api?.estadoActualSecundario === TipoEstadoActualSecundarioTrackingCorteEnum.NORMALIZADO;
  const esNormalizado = esCorteConcluido;

  const mostrarGraficoClientes = total !== null && recuperados !== null && sinServicio !== null;

  /**
   * El API trae un solo timestamp de brigada y se reutiliza según la etapa.
   */
  let horaAsignacion: Date | null = null;
  if (hasBrigadaAsignada || (esNormalizado && fechaArribo !== null)) {
    horaAsignacion = fechaArribo;
  }

  let horaTerreno: Date | null = null;
  if (
    api?.estadoActualSecundario === TipoEstadoActualSecundarioTrackingCorteEnum.BRIGADA_EN_TERRENO ||
    (esNormalizado && fechaArribo !== null)
  ) {
    horaTerreno = fechaArribo;
  }

  return {
    motivo_corte: normalizarMotivoCorte(api?.motivo),
    ter,
    etapa_actual: api?.estadoActualSecundario,
    hora_etapa: {
      corteConfirmado: parseFlexibleDate(api?.fechaInicioCorte),
      asignacionBrigada: horaAsignacion,
      brigadaEnTerreno: horaTerreno,
      reposicionEstimada: ter,
    },
    clientes_sin_servicio: sinServicio,
    clientes_recuperados: recuperados,
    total_afectados: total,
    ultima_actualizacion_mapa: parseFlexibleDate(api?.fechaActualizacion),
    hasBrigadaAsignada,
    tipoCorte: api?.tipo,
    numero_orden: numeroOrden,
    mostrarGraficoClientes,
    esCorteConcluido,
    tieneTer: ter !== null,
  };
};
